import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useLayoutEffect } from "react";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import { getUserConfig } from "app/api";
import { actions } from "app/configSlice";
import { AppDispatch, RootState } from "app/store";
import { DialogType, UserRestrictionFeature } from "app/types";
import { useUserRestriction, restrictionUntilText, isUserRestrictedError } from "app/restriction";
import I18n from "common/i18n";
import { IconPlayerPlay } from "common/assets";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { ConfirmMessageBox, WarningMessageBox } from "common/elements";
import { selectProjectById } from "features/creator/slice";
import { flushEditHistory } from "features/creator/editHistory";
import { BandageIcon, IconPublish } from "features/creator/assets";
import { PublishProjectDialogMessage } from "features/creator/elements";
import { EditProjectModal } from "features/creator/elements/EditProjectModal";
import { ProjectHistoryModal } from "features/creator/elements/ProjectHistoryModal";
import {
  publishProject,
  completeProject,
  flushProjectContent,
  updateProjectContentImmediately,
} from "features/creator/api";

export const AppMenu = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const [setting, setSetting] = useState(false);
  const [history, setHistory] = useState(false);
  const [collapse, setCollapse] = useState(false);
  const [publishUpdateConfirm, setPublishUpdateConfirm] = useState(false);
  const project_id = useSelector((state: RootState) => state.creator.editingProjectId);
  const project = useSelector((state: RootState) => selectProjectById(state, project_id));
  const user = useSelector((state: RootState) => state.user.active);
  // ボタンが増えたので、パネルの幅は実際の幅を測って出し入れする
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState(255);
  // 同じ位置のボタンを、公開していなければ「アプリを こうかい」、公開中なら「こうかいを こうしん」にする。
  // こうしんは直したときだけ押せる（押すとみんなに見える版が変わる）。公開していないアプリは直したらそのまま保存される
  const isPublished = !!project?.published;
  const canPublish = !!project && !project.deleted_at && !project.banned;
  const canPublishUpdate = isPublished && !!project?.has_draft;
  const enabled = isPublished ? canPublishUpdate : canPublish;
  const [publish, setPublish] = useState(false);
  const [warning, setWarning] = useState(false);
  const project_publish = useSelector(
    (state: RootState) => state.config.userConfig.project_publish,
  );
  // 運営が公開を止めているときは、保護者の許可より先にその理由を出す（プレビューの公開と同じ）
  const restriction = useUserRestriction(UserRestrictionFeature.PROJECT_PUBLISH);
  const [restrictedByApi, setRestrictedByApi] = useState(false);

  useLayoutEffect(() => {
    if (panelRef.current) setPanelWidth(panelRef.current.offsetWidth);
  }, []);

  const handleOpenPublish = () => {
    play();
    btnDelay(() => {
      if (restriction || !project_publish) {
        setWarning(true);
      } else {
        setPublish(true);
      }
    });
  };

  const handlePublish = () => {
    play();
    btnDelay(async () => {
      try {
        // 待っている保存を送り切ってから公開する（直前の変更が公開版から漏れないように）
        await flushProjectContent();
        await flushEditHistory();
        await dispatch(
          publishProject({
            uid: user.uid,
            project_id,
            enable_copied: project.enable_copied,
          }),
        ).unwrap();
      } catch (error) {
        setPublish(false);
        if (isUserRestrictedError(error)) {
          // 画面を開いたあとに運営が止めた。理由を出し、期限も出せるよう手元の制限を読み直す
          setRestrictedByApi(true);
          setWarning(true);
          dispatch(getUserConfig({ uid: user.uid }));
          return;
        }
        toast.warn(
          <div>
            <p className="type-text type-sm text-textcolor-black">{I18n.t("MSG_TOAST_FAILED")}</p>
          </div>,
        );
        return;
      }
      setPublish(false);
      toast.success(
        <div>
          <p className="type-text type-sm text-textcolor-black">
            {I18n.t("MSG_TOAST_PUBLISHED_SUCCESS")}
          </p>
        </div>,
      );
    });
  };

  const runPublishUpdate = async () => {
    try {
      // 待っている保存と変更履歴を送り切ってから更新する（直前の変更が公開版から漏れないように）
      await flushProjectContent();
      await flushEditHistory();
      await dispatch(completeProject({ uid: user.uid, project_id })).unwrap();
      toast.success(
        <div>
          <p className="type-text type-sm text-textcolor-black">
            {I18n.t("MSG_CREATOR_TOAST_PUBLISH_UPDATED")}
          </p>
        </div>,
      );
    } catch {
      toast.warn(
        <div>
          <p className="type-text type-sm text-textcolor-black">{I18n.t("MSG_TOAST_FAILED")}</p>
        </div>,
      );
    }
  };

  const handlePublishUpdate = () => {
    play();
    btnDelay(() => setPublishUpdateConfirm(true));
  };

  const handlePublishUpdateConfirm = () => {
    setPublishUpdateConfirm(false);
    runPublishUpdate();
  };

  const handleOpenHistory = () => {
    play();
    btnDelay(() => setHistory(true));
  };

  const handleCollapse = () => {
    play();
    setCollapse(!collapse);
  };

  const handleOpenSetting = () => {
    play();
    btnDelay(() => setSetting(true));
  };
  const handleCloseSetting = () => {
    play();
    setSetting(false);
  };

  const handleRunPreview = () => {
    play();
    dispatch(updateProjectContentImmediately({ uid: user.uid }));
    btnDelay(() =>
      dispatch(
        actions.updateDialog({
          type: DialogType.PROJECT_PREVIEW,
          value: true,
          args: { projectId: project_id, isUserProject: "true" },
        }),
      ),
    );
  };

  return (
    <div
      className="flex-row-center !absolute top-[85%] z-[1000] origin-top-right"
      style={{ right: -panelWidth * scale, transform: `scale(${scale})` }}
    >
      <EditProjectModal visible={setting} project={project} close={handleCloseSetting} />
      <ProjectHistoryModal visible={history} close={() => setHistory(false)} />
      {publishUpdateConfirm && (
        <ConfirmMessageBox
          title={I18n.t("MSG_CREATOR_PUBLISH_UPDATE_CONFIRM_TITLE")}
          message={I18n.t("MSG_CREATOR_PUBLISH_UPDATE_CONFIRM_MESSAGE")}
          close={() => setPublishUpdateConfirm(false)}
          confirm={handlePublishUpdateConfirm}
        />
      )}
      {warning && (
        <WarningMessageBox
          zIndex={9999999}
          title={I18n.t("MSG_CREATOR_APP_PUBLISH_DISABLED_TITLE")}
          message={
            restriction || restrictedByApi
              ? I18n.t("MSG_USER_RESTRICTION_PROJECT_PUBLISH_MESSAGE", {
                  until: restrictionUntilText(restriction),
                })
              : I18n.t("MSG_CREATOR_APP_PUBLISH_DISABLED_MESSAGE")
          }
          close={() => {
            play();
            btnDelay(() => setWarning(false));
          }}
        />
      )}
      <PublishProjectDialogMessage
        name={project?.name}
        visible={publish}
        published={false}
        enableCopied={project?.enable_copied}
        publish={handlePublish}
        close={() => {
          play();
          setPublish(false);
        }}
      />

      <div
        ref={panelRef}
        className="flex-row-view bg-white rounded-l-2xl p-2 transition-transform duration-500"
        style={{
          // 畳んだらつまみだけ見せる。見える幅はプレビューのメニューと同じ（実測で合わせた）。
          // 以前はパネルの端とボタンが少し見えていた
          transform: `translate(${collapse ? 10 : -panelWidth}px, 0)`,
          filter: "drop-shadow(3px 3px 3px rgba(63, 74, 97, 0.4))",
        }}
      >
        <div
          onClick={handleCollapse}
          className="flex-col-center w-[48px] h-[48px] !absolute top-[39px] left-[-48px] cursor-pointer"
        >
          <BandageIcon />
        </div>

        <div className="flex-col-el flex-center h-full relative upsilon">
          <button className="type-inherit-font btn btn-primary" onClick={handleOpenSetting}>
            <div className="flex-col-el flex-center relative mb-[3px]">
              <div className="flex-col-el flex-center relative w-[32px] h-[32px]">
                <EditCalendarIcon sx={{ fontSize: 28, color: "white" }} />
              </div>
              <p className="type-text type-sm text-white whitespace-pre">
                {I18n.t("MSG_CREATOR_MENU_SET_APP_BTN")}
              </p>
            </div>
          </button>
        </div>

        <div className="flex-col-el flex-center h-full relative upsilon">
          <button className="type-inherit-font btn btn-secondary" onClick={handleRunPreview}>
            <div className="flex-col-el flex-center relative mb-[3px]">
              <div className="flex-col-el flex-center relative w-[32px] h-[32px]">
                <IconPlayerPlay color="white" width={2} />
              </div>
              <p className="type-text type-sm text-white whitespace-pre">
                {I18n.t("MSG_CREATOR_MENU_RUN_APP_BTN")}
              </p>
            </div>
          </button>
        </div>

        <div className="flex-col-el flex-center h-full relative upsilon">
          <button className="type-inherit-font btn btn-primary" onClick={handleOpenHistory}>
            <div className="flex-col-el flex-center relative mb-1">
              <div className="flex-col-el flex-center relative w-8 h-8">
                <span className="material-symbols-outlined type-md text-white">history</span>
              </div>
              <p className="type-text type-sm text-white whitespace-pre">
                {I18n.t("MSG_CREATOR_MENU_HISTORY_BTN")}
              </p>
            </div>
          </button>
        </div>

        {/* 位置と幅は固定し、公開しているかで文言だけ変える（アプリを こうかい／こうかいを こうしん） */}
        <div className="flex-col-el flex-center h-full relative upsilon">
          <button
            disabled={!enabled}
            onClick={isPublished ? handlePublishUpdate : handleOpenPublish}
            className={`type-inherit-font btn w-36 ${
              enabled ? (isPublished ? "btn-secondary" : "btn-blue") : "btn-gray"
            }`}
          >
            <div className="flex-col-el flex-center relative mb-1">
              <div className="flex-col-el flex-center relative w-8 h-8">
                {/* 公開はプレビュー（うごかす）の公開ボタンと同じアイコン */}
                {isPublished ? (
                  <span className="material-symbols-outlined type-md text-white">
                    published_with_changes
                  </span>
                ) : (
                  <IconPublish fill="none" stroke="#FFF" />
                )}
              </div>
              <p className="type-text type-sm text-white whitespace-pre">
                {I18n.t(
                  isPublished
                    ? "MSG_CREATOR_MENU_PUBLISH_UPDATE_BTN"
                    : "MSG_CREATOR_MENU_PUBLISH_APP_BTN",
                )}
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
